import { handleRequest } from "../utilities/requestHandler";

export const getSitesByCustomerID = (customerId) =>
    handleRequest("get",`/cus_sites/getsites/${customerId}`);

export const getMachinesBySiteId = (siteNumber) =>
    handleRequest("post","/machines/bysiteid",{site_number:siteNumber});

export const getProvisionedMachines = ({ includeInactive } = {}) => {
    const params = new URLSearchParams();
    if (includeInactive) params.set("include_inactive", "true");
    const query = params.toString();
    const url = query ? `/machines/by-provision?${query}` : "/machines/by-provision";
    return handleRequest("get", url);
};

export const provisionMachinesForCustomerSite = (req_body) =>
    handleRequest("post","/cus_sites/provisionmachines",{table_data: req_body});

export const deleteProvisionsForCustomerSite = (req_body) =>
    handleRequest("post", "/cus_sites/delete-provisions", req_body);

export const updateProvisionForCustomerSite = (id, data) =>
    handleRequest("put", `/cus_sites/provisions/${id}`, data);

export const triggerDocumentSyncForMachine = (
    machineNumber,
    docSyncThreads = 4,
    syncMode = "auto"
) =>
    handleRequest("post", "/documents/sync/machine-fetch", {
        machine_number: machineNumber,
        doc_sync_threads: docSyncThreads,
        sync_mode: syncMode,
    });

/**
 * Queue a 3D/STEP reload for one machine.
 *
 * @param {number} siteId Site the machine is provisioned at.
 * @param {string} machineNumber Machine to reload.
 * @param {"simple"|"hard"} [mode="simple"] "simple" keeps the server-side freshness
 *   check (unchanged source + model still on disk = skipped as up to date). "hard"
 *   rebuilds the model regardless, for when the published file is wrong or was deleted.
 * @returns {Promise<object>} Envelope whose data holds the queued request row.
 */
export const triggerReload3DModelForMachine = (siteId, machineNumber, mode = "simple") =>
    handleRequest("post", "/machines/reload-3d-model", {
        site_id: siteId,
        machine_number: machineNumber,
        mode,
    });

/**
 * Queue a 3D/STEP reload for many machines in a single request.
 *
 * The per-machine endpoint is still there for the single row action; this one exists
 * so the bulk "Refresh STEP Files" button does not fire one HTTP call per selected
 * machine. Server caps a batch at 500 — callers chunk above that.
 *
 * @param {{site_id: number, machine_number: string}[]} machines Pairs to queue.
 * @param {"simple"|"hard"} [mode="simple"] Applies to the whole batch — the prompt is
 *   answered once, not per machine.
 * @returns {Promise<object>} Envelope whose data holds {requested, queued, skipped_already_active}.
 */
export const triggerBulkReload3DModelForMachines = (machines, mode = "simple") =>
    handleRequest("post", "/machines/reload-3d-model/bulk", { machines, mode });

/**
 * Queue a STEP sync for every machine at one site.
 *
 * @param {number} siteId Site to sync.
 * @param {"simple"|"hard"} [mode="simple"] "hard" rebuilds every model at the site, so
 *   it is by far the most expensive option here — every machine goes to the converter.
 * @returns {Promise<object>} Envelope whose data holds {total_machines, queued, skipped_already_active}.
 */
export const triggerStepSyncForSite = (siteId, mode = "simple") =>
    handleRequest("post", "/machines/site-step-sync", {
        site_id: siteId,
        mode,
    });
